import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Rocket, X, Check, ChevronRight } from 'lucide-react';
import { usePartnerSettings } from '@/hooks/useSettings';
import { useBalance } from '@/hooks/useBalance';
import { useCatalogList } from '@/hooks/useCatalog';

interface Step {
  key: string;
  label: string;
  done: boolean;
  to: string;
  cta: string;
}

interface PartnerNextStepsCardProps {
  partnerId: string;
  hasCampaign: boolean;
}

export function PartnerNextStepsCard({ partnerId, hasCampaign }: PartnerNextStepsCardProps) {
  const { data: settings, isError: settingsError, isLoading: settingsLoading } = usePartnerSettings();
  const { data: balance, isError: balanceError, isLoading: balanceLoading } = useBalance();
  const {
    data: catalog,
    isError: catalogError,
    isLoading: catalogLoading,
  } = useCatalogList(partnerId, { limit: 1 });

  const dismissKey = `mustauto:partner_nextsteps_dismissed:${partnerId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey) === 'true';
    } catch {
      return false;
    }
  });

  function dismiss() {
    try {
      localStorage.setItem(dismissKey, 'true');
    } catch {
      // ignore
    }
    setDismissed(true);
  }

  const steps: Step[] = [
    { key: 'company', label: 'Dados da empresa', done: true, to: '/settings', cta: 'Editar' },
    {
      key: 'profile',
      label: 'Completar perfil (CNPJ, faturamento)',
      done: !!settings?.cnpj,
      to: '/settings',
      cta: 'Completar',
    },
    {
      key: 'balance',
      label: 'Adicionar saldo',
      done: (balance?.currentBalance ?? 0) > 0,
      to: '/balance',
      cta: 'Depositar',
    },
    {
      key: 'catalog',
      label: 'Cadastrar catálogo / vitrine',
      done: (catalog?.total ?? 0) > 0,
      to: '/catalog',
      cta: 'Cadastrar',
    },
    {
      key: 'campaign',
      label: 'Criar sua primeira campanha',
      done: hasCampaign,
      to: '/campaigns/new',
      cta: 'Criar',
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  // Cada passo e marcado por ausencia: `!!settings?.cnpj`,
  // `(balance?.currentBalance ?? 0) > 0`, `(catalog?.total ?? 0) > 0`. Com a
  // busca falhada os tres viram `false`, e o cartao dizia a um parceiro pronto
  // que "faltam 4 de 5 passos para sua marca aparecer" — mandando-o refazer o
  // que ja fez. Este cartao so pode falar quando sabe; nao havendo dado, some.
  // Vale para o carregando tambem, nao so para o erro: com as buscas em voo os
  // tres checks leem `false` e o cartao pisca "Faltam 4 de 5 passos" a cada
  // abertura do painel, para quem ja terminou o onboarding.
  const unknownData =
    ((settingsError || settingsLoading) && !settings) ||
    ((balanceError || balanceLoading) && !balance) ||
    ((catalogError || catalogLoading) && !catalog);

  if (dismissed || allDone || unknownData) return null;

  return (
    <div className="rounded-xl border border-primary/30 border-l-4 border-l-primary bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Rocket size={18} className="text-primary" />
          <h2 className="text-base font-bold">Comece por aqui</h2>
        </div>
        <button
          onClick={dismiss}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          dispensar <X size={13} />
        </button>
      </div>

      <p className="mt-1 text-sm text-muted-foreground">
        Faltam {steps.length - doneCount} de {steps.length} passos para sua marca aparecer.
      </p>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      <ul className="mt-4 space-y-2.5">
        {steps.map((step) => (
          <li key={step.key} className="flex items-center gap-3">
            <span
              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
                step.done ? 'border-success bg-success text-white' : 'border-border'
              }`}
            >
              {step.done && <Check size={12} strokeWidth={3} />}
            </span>
            <span
              className={`flex-1 text-sm ${
                step.done ? 'text-muted-foreground line-through' : 'text-foreground'
              }`}
            >
              {step.label}
            </span>
            {!step.done && (
              <Link
                to={step.to}
                className="flex items-center gap-0.5 text-xs font-semibold text-primary hover:underline"
              >
                {step.cta} <ChevronRight size={13} />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
